// "tags" cluster: Settings > Console Settings > Tags (src/pages/settings/TagsPage.tsx), ADR-033 D8/D10,
// RepairList OP-1096 section 140. Spread into the central LABELS in labels.ts.
//
// A tag has ONE English label (D10): the label the admin types is shown as typed in both UI languages, so
// there is no Hebrew label field - only this chrome is bilingual. The portal's `t()` takes no variables, so
// `{name}`, `{n}` and `{resource}` are substituted by the caller with .replace().
//
// Usage-aware confirmation text has separate One / Many / None / Unknown keys instead of one pluralised
// sentence: Hebrew plural agreement cannot be done by string substitution.
export const TAGS_LABELS = {
  tagsTitle: { en: 'Tags', he: 'תגיות' },
  tagsSubtitle: {
    en: 'Labels that agents attach to conversations in the Agent Console. Agents can attach and remove these tags but cannot create new ones.',
    he: 'תוויות שהנציגים מצמידים לשיחות בקונסולת הנציגים. נציגים יכולים להוסיף ולהסיר תגיות אלה, אך לא ליצור תגיות חדשות.',
  },
  tagsNew: { en: 'New tag', he: 'תגית חדשה' },
  tagsShowInactive: { en: 'Show inactive tags', he: 'הצג תגיות לא פעילות' },
  tagsLoading: { en: 'Loading tags…', he: 'טוען תגיות…' },
  tagsLoadError: { en: 'Failed to load tags.', he: 'טעינת התגיות נכשלה.' },
  tagsEmpty: {
    en: 'No tags yet. Create the first tag so agents can attach it to conversations.',
    he: 'אין תגיות עדיין. צור את התגית הראשונה כדי שנציגים יוכלו להצמיד אותה לשיחות.',
  },
  tagsEmptyActive: {
    en: 'No active tags. Turn on "Show inactive tags" to see deactivated ones.',
    he: 'אין תגיות פעילות. הפעל את "הצג תגיות לא פעילות" כדי לראות תגיות שהושבתו.',
  },
  tagsReadOnlyNote: {
    en: 'You can view tags but you need the "edit configuration" permission to change them.',
    he: 'ניתן לצפות בתגיות, אך שינוי שלהן דורש הרשאת "עריכת תצורה".',
  },

  // Table
  tagsColTag: { en: 'Tag', he: 'תגית' },
  tagsColKey: { en: 'Key', he: 'מזהה' },
  tagsColUsage: { en: 'Conversations', he: 'שיחות' },
  tagsColStatus: { en: 'Status', he: 'סטטוס' },
  tagsColActions: { en: 'Actions', he: 'פעולות' },
  tagsStatusActive: { en: 'Active', he: 'פעילה' },
  tagsStatusInactive: { en: 'Inactive', he: 'לא פעילה' },
  tagsUsageUnavailable: { en: 'Usage unavailable', he: 'נתוני שימוש לא זמינים' },
  tagsUsageUnavailableNote: {
    en: 'Usage counts could not be loaded.',
    he: 'לא ניתן היה לטעון את מספר השימושים.',
  },

  // Row actions (aria-labels name the tag so a screen-reader list of buttons is distinguishable)
  tagsEditAria: { en: 'Edit tag {name}', he: 'ערוך את התגית {name}' },
  tagsDeactivate: { en: 'Deactivate', he: 'השבת' },
  tagsDeactivateAria: { en: 'Deactivate tag {name}', he: 'השבת את התגית {name}' },
  tagsReactivate: { en: 'Reactivate', he: 'הפעל מחדש' },
  tagsReactivateAria: { en: 'Reactivate tag {name}', he: 'הפעל מחדש את התגית {name}' },

  // Form
  tagsFormCreateTitle: { en: 'New tag', he: 'תגית חדשה' },
  tagsFormEditTitle: { en: 'Edit tag', he: 'עריכת תגית' },
  tagsFieldLabel: { en: 'Label', he: 'תווית' },
  tagsFieldLabelHint: {
    en: 'English only. 1 to 40 characters; commas and semicolons are not allowed.',
    he: 'באנגלית בלבד. 1 עד 40 תווים; פסיקים ונקודה-פסיק אינם מותרים.',
  },
  tagsFieldColor: { en: 'Color', he: 'צבע' },
  tagsFieldColorHex: { en: 'Color code (#rrggbb)', he: 'קוד צבע (#rrggbb)' },
  tagsColorOptionAria: { en: 'Color {color}', he: 'צבע {color}' },
  tagsFieldKeyReadonly: { en: 'Key (cannot be changed)', he: 'מזהה (לא ניתן לשינוי)' },

  // Client-side validation (mirrors the server rules)
  tagsErrLabelRequired: { en: 'Enter a label.', he: 'יש להזין תווית.' },
  tagsErrLabelTooLong: {
    en: 'The label can be at most 40 characters.',
    he: 'התווית יכולה להכיל עד 40 תווים.',
  },
  tagsErrLabelForbiddenChars: {
    en: 'The label cannot contain a comma or a semicolon.',
    he: 'התווית לא יכולה להכיל פסיק או נקודה-פסיק.',
  },
  tagsErrColorInvalid: {
    en: 'Enter a color as #rrggbb, for example #3b82f6.',
    he: 'יש להזין צבע בפורמט #rrggbb, למשל #3b82f6.',
  },

  // Errors returned by the server
  tagsErrInvalidLabel: {
    en: 'The server rejected this label. Use 1 to 40 characters without commas or semicolons.',
    he: 'השרת דחה את התווית. השתמש ב-1 עד 40 תווים ללא פסיקים או נקודה-פסיק.',
  },
  tagsErrInvalidColor: {
    en: 'The server rejected this color. Use the #rrggbb format.',
    he: 'השרת דחה את הצבע. השתמש בפורמט #rrggbb.',
  },
  tagsErrInvalid: {
    en: 'The server rejected this tag. Check the label and color.',
    he: 'השרת דחה את התגית. בדוק את התווית והצבע.',
  },
  tagsErrDuplicate: {
    en: 'A tag with this label already exists. Choose a different label.',
    he: 'כבר קיימת תגית עם תווית זו. בחר תווית אחרת.',
  },
  tagsErrNotFound: {
    en: 'This tag no longer exists. The list has been refreshed.',
    he: 'התגית כבר לא קיימת. הרשימה רועננה.',
  },
  tagsErrForbidden: {
    en: 'You do not have permission to change tags.',
    he: 'אין לך הרשאה לשנות תגיות.',
  },
  tagsResourceName: { en: 'the tag', he: 'התגית' },

  // Deactivate confirmation (names the usage count; the tag stays on conversations that already carry it)
  tagsDeactivateTitle: { en: 'Deactivate tag', he: 'השבתת תגית' },
  tagsDeactivateConfirmMany: {
    en: 'Tag "{name}" is used on {n} conversations. They keep the tag, but it stops being offered to agents.',
    he: 'התגית "{name}" משמשת ב-{n} שיחות. הן ישמרו את התגית, אך היא תפסיק להיות מוצעת לנציגים.',
  },
  tagsDeactivateConfirmOne: {
    en: 'Tag "{name}" is used on 1 conversation. It keeps the tag, but the tag stops being offered to agents.',
    he: 'התגית "{name}" משמשת בשיחה אחת. השיחה תשמור את התגית, אך היא תפסיק להיות מוצעת לנציגים.',
  },
  tagsDeactivateConfirmNone: {
    en: 'Tag "{name}" is not used on any conversation. It stops being offered to agents.',
    he: 'התגית "{name}" אינה בשימוש באף שיחה. היא תפסיק להיות מוצעת לנציגים.',
  },
  tagsDeactivateConfirmUnknown: {
    en: 'Tag "{name}" will stop being offered to agents. Conversations that already carry it keep it.',
    he: 'התגית "{name}" תפסיק להיות מוצעת לנציגים. שיחות שכבר נושאות אותה ישמרו אותה.',
  },

  // Active-tag cap (409 too_many_catalog_tags)
  tagsErrTooManyTags: {
    en: 'The tag limit (100 active tags) has been reached. Deactivate a tag you no longer use first.',
    he: 'הגעת למגבלת התגיות (100 תגיות פעילות). יש להשבית קודם תגית שאינה בשימוש.',
  },

  // CRM sync status (ADR-033 D11): the page-level hint and the per-tag column. `{p}` = the CRM property name.
  // OP-1507: this page cannot see the CRM master write switches, so "synced" must say it depends on them.
  tagsCrmStatusOn: {
    en: 'Synced to CRM property {p} when CRM writing is switched on (Settings > CRM)',
    he: 'מסונכרן עם תכונת ה-CRM {p} כאשר הכתיבה ל-CRM מופעלת (הגדרות > CRM)',
  },
  tagsCrmOpenMapping: { en: 'Open Property Mapping', he: 'פתיחת מיפוי שדות CRM' },
  tagsCrmStatusOff: {
    en: 'Mapping exists but is off - enable it in Settings > Property Mapping',
    he: 'המיפוי קיים אך כבוי - יש להפעיל אותו בהגדרות > מיפוי שדות CRM',
  },
  tagsCrmStatusNone: { en: 'Not synced to the CRM', he: 'לא מסונכרן עם ה-CRM' },
  tagsCrmStatusUnknown: {
    en: 'The CRM sync status could not be loaded.',
    he: 'לא ניתן היה לטעון את מצב הסנכרון עם ה-CRM.',
  },
  tagsCrmColumnHeader: { en: 'CRM sync', he: 'סנכרון CRM' },
  tagsCrmColSynced: { en: 'Synced', he: 'מסונכרנת' },
  tagsCrmColOff: { en: 'Mapped, sync is off', he: 'ממופה, הסנכרון כבוי' },
  tagsCrmColNone: { en: 'Not mapped', he: 'לא ממופה' },

  // Import from a CRM property
  crmTagImportInlineTitle: { en: 'Bring these options into BotableX as tags', he: 'הבאת האפשרויות האלה ל-BotableX כתגיות' },
  crmTagImportInlineIntro: {
    en: 'Creates a BotableX tag for each of the {n} options of "{p}" and connects every tag to its option in the value map, in one step. Tags that already exist are reused, so running it again only adds options that are new in the CRM.',
    he: 'יוצר תגית ב-BotableX לכל אחת מ-{n} האפשרויות של "{p}" ומחבר כל תגית לאפשרות שלה במיפוי הערכים, בצעד אחד. תגיות שכבר קיימות משמשות שוב, כך שהרצה חוזרת מוסיפה רק אפשרויות חדשות ב-CRM.',
  },
  crmTagImportInlineSubmit: { en: 'Import {n} options as tags', he: 'ייבוא {n} אפשרויות כתגיות' },
  crmTagImportInlineUnsaved: {
    en: 'Save or discard your other changes on this page first. The import saves the mapping, so it would overwrite them.',
    he: 'שמור או בטל קודם את השינויים האחרים בעמוד. הייבוא שומר את המיפוי, ולכן היה דורס אותם.',
  },
  crmTagImportInlineReplacesEditor: {
    en: 'The import saves the tags row straight away; anything not yet applied in this editor is replaced.',
    he: 'הייבוא שומר את שורת התגיות מיד; כל מה שעדיין לא הוחל בעורך זה יוחלף.',
  },
  crmTagImportInlineConnected: {
    en: 'Every tag is now connected to its CRM option in the value map below.',
    he: 'כל תגית מחוברת כעת לאפשרות שלה ב-CRM במיפוי הערכים שלהלן.',
  },
  crmTagImportInlineRowOff: {
    en: 'The tags row is OFF, so nothing is sent to the CRM yet. Turn it on in the table below and save when you are ready.',
    he: 'שורת התגיות כבויה, ולכן עדיין לא נשלח דבר ל-CRM. הפעל אותה בטבלה שלהלן ושמור כשתהיה מוכן.',
  },
  tagsImportTitle: { en: 'Import from CRM property', he: 'ייבוא מתכונת CRM' },
  tagsImportIntro: {
    en: 'Turn the options of a CRM ticket property into tags and bind them in the property mapping. Nothing is written to the CRM by importing; the mapping row stays off until you enable it.',
    he: 'הפיכת האפשרויות של תכונת קריאה ב-CRM לתגיות וקישורן במיפוי השדות. הייבוא אינו כותב דבר ל-CRM; שורת המיפוי נשארת כבויה עד שתפעיל אותה.',
  },
  tagsImportOpen: { en: 'Import from CRM property', he: 'ייבוא מתכונת CRM' },
  tagsImportClose: { en: 'Close', he: 'סגירה' },
  tagsImportProperty: { en: 'CRM ticket property', he: 'תכונת קריאה ב-CRM' },
  tagsImportChoose: { en: 'Choose a property', he: 'בחירת תכונה' },
  tagsImportNoProperties: {
    en: 'The discovered CRM schema has no writable list property to import from.',
    he: 'בסכימת ה-CRM שנסרקה אין תכונת רשימה ניתנת לכתיבה לייבוא ממנה.',
  },
  tagsImportLoadingSchema: { en: 'Loading the CRM schema…', he: 'טוען את סכימת ה-CRM…' },
  tagsImportSchemaFailed: {
    en: 'The CRM schema could not be loaded.',
    he: 'לא ניתן היה לטעון את סכימת ה-CRM.',
  },
  tagsImportOptionsHeading: { en: 'Options that will become tags', he: 'אפשרויות שיהפכו לתגיות' },
  tagsImportOptionHidden: { en: 'hidden in the CRM', he: 'מוסתרת ב-CRM' },
  tagsImportIncludeHidden: { en: 'Include options that are hidden in the CRM', he: 'לכלול אפשרויות שמוסתרות ב-CRM' },
  tagsImportSubmit: { en: 'Import tags', he: 'ייבוא תגיות' },
  tagsImporting: { en: 'Importing…', he: 'מייבא…' },
  tagsImportResult: {
    en: 'Imported: {created} tag(s) created, {matched} already matched.',
    he: 'הייבוא הושלם: {created} תגיות נוצרו, {matched} כבר היו תואמות.',
  },
  tagsImportRemoved: {
    en: '{n} value-map entries pointed at options that are no longer in the CRM and were removed. The tags themselves are kept.',
    he: '{n} רשומות במיפוי הערכים הצביעו על אפשרויות שכבר אינן ב-CRM והוסרו. התגיות עצמן נשמרו.',
  },
  tagsImportRowDisabled: {
    en: 'The mapping row is OFF. Enable it in Settings > Property Mapping when you want tags sent to the CRM.',
    he: 'שורת המיפוי כבויה. הפעל אותה בהגדרות > מיפוי שדות CRM כשתרצה שתגיות יישלחו ל-CRM.',
  },
  tagsImportRowEnabled: {
    en: 'The mapping row is ON: tag changes on conversations are written to the CRM when CRM writing is switched on.',
    he: 'שורת המיפוי פעילה: שינויי תגיות בשיחות נכתבים ל-CRM כאשר הכתיבה ל-CRM מופעלת.',
  },
  tagsImportErrRowTargetsOther: {
    en: 'The property mapping already has a tags row that points at a different CRM property. Change or remove that row in Property Mapping, save, and import again.',
    he: 'במיפוי השדות כבר יש שורת תגיות שמצביעה על תכונת CRM אחרת. יש לשנות או להסיר את השורה במיפוי השדות, לשמור ולייבא שוב.',
  },
  tagsImportErrMappingChanged: {
    en: 'The property mapping was saved by someone else while this import ran, so nothing was added to it. Run the import again; repeating it is safe.',
    he: 'מיפוי השדות נשמר על ידי מישהו אחר בזמן הייבוא, ולכן לא נוסף אליו דבר. יש להריץ את הייבוא שוב; חזרה עליו בטוחה.',
  },
  tagsImportErrCatalogUnavailable: {
    en: 'The tag list could not be reached or did not finish the import. Some of the options may already have been created as tags, but nothing was added to the property mapping. Run the import again; repeating it is safe.',
    he: 'לא ניתן היה לגשת לרשימת התגיות או שהייבוא לא הסתיים. ייתכן שחלק מהאפשרויות כבר נוצרו כתגיות, אך לא נוסף דבר למיפוי השדות. יש להריץ את הייבוא שוב; חזרה עליו בטוחה.',
  },
  tagsImportErrNoCatalog: {
    en: 'Refresh the CRM schema in Settings > CRM first.',
    he: 'יש לרענן קודם את סכימת ה-CRM בהגדרות > CRM.',
  },
  tagsImportErrValidation: {
    en: 'The mapping was not saved. Fix these and try again:',
    he: 'המיפוי לא נשמר. יש לתקן את הבאים ולנסות שוב:',
  },
  tagsImportErrGeneric: {
    en: 'The import failed. The property mapping was not changed; some tags may already have been created, and repeating the import is safe.',
    he: 'הייבוא נכשל. מיפוי השדות לא שונה; ייתכן שחלק מהתגיות כבר נוצרו, ואפשר לחזור על הייבוא בבטחה.',
  },
  tagsImportErrCapReached: {
    en: 'The tenant already has the maximum number of active tags, so the import stopped part-way. Some options may already have been created as tags, but nothing was added to the property mapping. Deactivate tags that are no longer used, or import fewer options, and run the import again.',
    he: 'בארגון כבר יש את המספר המרבי של תגיות פעילות, ולכן הייבוא נעצר באמצע. ייתכן שחלק מהאפשרויות כבר נוצרו כתגיות, אך דבר לא נוסף למיפוי השדות. יש להשבית תגיות שאינן בשימוש, או לייבא פחות אפשרויות, ולהריץ שוב את הייבוא.',
  },
  tagsImportErrCrmPropertyRequired: { en: 'Choose a CRM property to import from.', he: 'יש לבחור תכונת CRM לייבוא.' },
  tagsImportErrUnknownProperty: {
    en: 'That property is not in the discovered CRM schema. Refresh the schema and try again.',
    he: 'התכונה אינה בסכימת ה-CRM שנסרקה. יש לרענן את הסכימה ולנסות שוב.',
  },
  tagsImportErrNotEnumeration: { en: 'Only list (enumeration) properties can be imported.', he: 'ניתן לייבא רק מתכונות מסוג רשימה.' },
  tagsImportErrNotWritable: { en: 'That property is read-only in the CRM.', he: 'התכונה היא לקריאה בלבד ב-CRM.' },
  tagsImportErrReserved: {
    en: 'That property is reserved by the integration and cannot hold tags.',
    he: 'התכונה שמורה לאינטגרציה ולא יכולה להחזיק תגיות.',
  },
  tagsImportErrTooManyOptions: {
    en: 'That property has too many options to import (maximum 200).',
    he: 'בתכונה יש יותר מדי אפשרויות לייבוא (מקסימום 200).',
  },
  tagsImportErrNoOptions: { en: 'That property has no options to import.', he: 'לתכונה אין אפשרויות לייבוא.' },
} satisfies Record<string, { en: string; he: string }>;
